import type { ReactNode } from 'react';
import { money, LockIcon } from '@wyocspay/ui';
import type { Strings } from './i18n.js';
import { PAY_TOKENS } from './Shell.js';
import { COMPANY } from './legal.js';

const { NAVY, INK, MUTED, FAINT, LINE } = PAY_TOKENS;
const GREEN = '#2d7a4a';
const RED = '#c02929';

export type ResultPhase = 'processing' | 'ok' | 'declined';

/**
 * The animated Result step (step 3), shared by the customer checkout and the
 * subscription flow. Purely presentational: the caller decides the phase from
 * the real outcome. The "bank / 3-D Secure / authorize" chips are pacing, not a
 * live processor trace.
 */
export function ResultView({
  phase, authorized = null, amount, currency, merchant, merchantLogo, paymentId, message, receiptAt,
  timedOut = false, onRefresh, aside,
  t, locale, onRetry, onChangeMethod,
}: {
  phase: ResultPhase;
  /**
   * Whether Stripe authorized the charge, from the REAL confirm result: true =
   * the bank authorized it, false/null = still authorizing. Drives the step
   * chips honestly rather than on a timer.
   */
  authorized?: boolean | null;
  amount: number | null;
  currency: string;
  merchant: string;
  /** The merchant's own logo (data URI), shown beside their name. Null for none. */
  merchantLogo?: string | null;
  paymentId?: string | null;
  message?: string | null;
  declineCode?: string | null;
  receiptAt?: string | null;
  /**
   * True when we have waited past the timeout for a verified outcome and still
   * have none. The screen stops implying imminent completion and offers a
   * manual re-check instead of spinning forever - but it never invents a
   * result, so the phase stays `processing`.
   */
  timedOut?: boolean;
  onRefresh?: () => void;
  /**
   * Optional content for the right-hand column - the customer flow passes the
   * advertising slot here. It renders below the reassurance card and self-hides
   * when nothing is booked, so the column is never empty.
   */
  aside?: ReactNode;
  t: Strings;
  locale: string;
  onRetry?: () => void;
  onChangeMethod?: () => void;
}) {
  const x = t as unknown as Record<string, string>;
  const bandBg = phase === 'ok' ? '#eaf5ee' : phase === 'declined' ? '#faf0e8' : '#eef4f9';
  const bandLine = phase === 'ok' ? GREEN : phase === 'declined' ? RED : NAVY;
  const head = phase === 'ok'
    ? (t.okHead ?? 'Payment approved')
    : phase === 'declined'
      ? (t.failHead ?? 'Payment declined')
      : timedOut
        ? (x.timeoutHead ?? 'Still confirming your payment')
        : (x.pendingHead ?? 'Processing your payment');
  const body = phase === 'ok'
    ? `${merchant ? merchant + ' ' : ''}${x.okBodyShort ?? (t.okBody ?? 'has been paid. A receipt is on its way to your inbox.')}`
    : phase === 'declined'
      ? (message ?? t.failBody ?? 'This transaction could not be completed. No money has left your account.')
      : timedOut
        ? (x.timeoutSub ?? 'This is taking longer than usual. Your payment is not lost - you can check again below.')
        : (x.pendingSub ?? 'Please keep this window open - do not refresh or close it.');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 300px', gap: 34, alignItems: 'start' }} className="sp-result">
      <style>{`
        @keyframes sp-spin { to { transform: rotate(360deg); } }
        @keyframes sp-pulse { 0%,100% { opacity:.4 } 50% { opacity:1 } }
        @media (max-width: 760px){ .sp-result{ grid-template-columns: 1fr !important; } }
      `}</style>

      <div style={{ background: '#fff', border: `1px solid ${LINE}`, boxShadow: 'rgba(38,49,58,0.05) 0 1px 2px' }}>
        <div style={{ background: INK, color: '#fff', padding: '16px 26px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 5, height: 18, background: GREEN, display: 'inline-block' }} />
          <span style={{ fontSize: 13, letterSpacing: '0.09em', textTransform: 'uppercase', fontWeight: 700 }}>{x.resultBand ?? 'Payment to merchant'}</span>
        </div>

        <div style={{ padding: '24px 26px 28px' }}>
          {/* The merchant's own brand, so the payer sees who they paid. */}
          {merchantLogo ? (
            <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: `1px solid ${LINE}`, textAlign: 'center' }}>
              <img src={merchantLogo} alt={merchant} style={{ height: 44, maxWidth: '100%', width: 'auto', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            </div>
          ) : null}
          <h1 style={{ fontSize: 22, margin: '0 0 4px', color: INK, fontWeight: 700 }}>{x.payInfo ?? 'Payment information'}</h1>
          <div style={{ borderBottom: `2px solid ${INK}`, margin: '10px 0 6px' }} />
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15.5 }}>
            <tbody>
              <InfoRow label={t.merchant ?? 'Merchant'} value={merchant} />
              {paymentId ? <InfoRow label={x.txnLabel ?? 'Transaction'} value={paymentId} mono /> : null}
              {amount !== null ? <InfoRow label={phase === 'ok' ? (x.chargedLabel ?? 'Amount charged') : phase === 'declined' ? (x.chargedLabelNo ?? 'Amount attempted') : (t.total ?? 'Amount')} value={money(amount, currency, locale)} mono bold /> : null}
            </tbody>
          </table>

          <div style={{ marginTop: 18, background: bandBg, borderTop: `3px solid ${bandLine}`, borderBottom: `1px solid ${bandLine}55`, display: 'flex', alignItems: 'center', gap: 18, padding: '16px 20px' }}>
            <div style={{ minWidth: 90, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED }}>{x.statusLabel ?? 'Status'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {phase === 'processing'
                ? <span aria-hidden style={{ width: 30, height: 30, border: `3px solid ${bandLine}33`, borderTopColor: bandLine, borderRadius: '50%', animation: 'sp-spin .9s linear infinite', flex: 'none' }} />
                : <span aria-hidden style={{ width: 30, height: 30, borderRadius: '50%', background: bandLine, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, flex: 'none' }}>{phase === 'ok' ? '✓' : '✕'}</span>}
              <div>
                <div role="status" aria-live="polite" style={{ fontWeight: 700, fontSize: 16, color: bandLine }}>{head}</div>
                <div style={{ fontSize: 14, color: INK, marginTop: 2 }}>{body}</div>
              </div>
            </div>
          </div>

          {/* Real steps, from the actual outcome: the payment was submitted, the
              bank authorized (or declined) it, and WyoCSPay confirmed it from a
              verified server event. Nothing is on a timer. */}
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', margin: '16px 0 4px' }}>
            <Chip
              state="done"
              label={x.stepSubmitted ?? 'Payment submitted'}
            />
            <Chip
              state={phase === 'declined' ? 'fail' : authorized || phase === 'ok' ? 'done' : 'active'}
              label={x.stepAuthorized ?? 'Authorized by your bank'}
            />
            <Chip
              state={phase === 'ok' ? 'done' : phase === 'declined' ? 'na' : authorized ? 'active' : 'pending'}
              label={x.stepConfirmed ?? 'Confirmed by WyoCSPay'}
            />
          </div>

          {phase === 'ok' ? (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 13.5, color: MUTED, margin: '0 0 6px' }}>{x.okHint ?? 'A receipt has been emailed to you. You can safely return to the merchant.'}</p>
              {receiptAt ? <p className="wc-num" style={{ fontSize: 12.5, color: FAINT, margin: 0 }}>{receiptAt}</p> : null}
            </div>
          ) : phase === 'declined' ? (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 13.5, color: MUTED, margin: '0 0 12px' }}>{t.failHint ?? 'No money has left your account. If the problem repeats, contact your card issuer or try a different payment method.'}</p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {onRetry ? <button type="button" onClick={onRetry} style={btnPrimary}>{t.tryAgain ?? 'Try again'}</button> : null}
                {onChangeMethod ? <button type="button" onClick={onChangeMethod} style={btnGhost}>{x.chooseAnother ?? 'Choose another method'}</button> : null}
              </div>
            </div>
          ) : timedOut ? (
            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 13.5, color: MUTED, margin: '0 0 12px' }}>{x.timeoutHint ?? 'We have not had a confirmation from your bank yet. This can take a few minutes. Check again now, or you will get an email the moment it settles - no need to pay again.'}</p>
              {onRefresh ? <button type="button" onClick={onRefresh} style={btnPrimary}>{x.checkAgain ?? 'Check again'}</button> : null}
            </div>
          ) : (
            <p style={{ fontSize: 13, color: FAINT, marginTop: 16 }}>{x.pendingKeep ?? 'Please keep this window open - do not refresh or close it.'}</p>
          )}
        </div>
      </div>

      <div>
        {/* A quiet reassurance card, not a wall of card-brand tiles and a raw
            transaction id. The id already sits in the details table on the
            left, labelled; repeating it here in full only cluttered the page. */}
        <div style={{ background: '#fff', border: `1px solid ${LINE}`, boxShadow: 'rgba(38,49,58,0.05) 0 1px 2px' }}>
          <div style={{ padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span aria-hidden style={{ display: 'grid', placeItems: 'center', width: 34, height: 34, background: '#eef4f9', color: NAVY, flex: 'none' }}><LockIcon size={16} /></span>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: INK }}>{x.securedTitle ?? 'Secured by WyoCSPay'}</span>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 13, color: MUTED, lineHeight: 1.5 }}>{x.securedBody ?? 'Processed over an encrypted, PCI DSS Level 1 connection. Your card details were entered directly with the bank and never touched this page.'}</p>
            <p style={{ margin: 0, fontSize: 12.5, color: FAINT, lineHeight: 1.5 }}>
              {x.supportQ ?? 'Questions about this charge? Reach support at'} <a href={`mailto:${COMPANY.email}`} style={{ color: '#2f6188' }}>{COMPANY.email}</a>
            </p>
          </div>
        </div>
        {aside}
      </div>
    </div>
  );
}

const btnPrimary: React.CSSProperties = { background: NAVY, color: '#fff', border: 'none', padding: '13px 26px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
const btnGhost: React.CSSProperties = { background: '#fff', color: INK, border: `1px solid ${LINE}`, padding: '13px 26px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

function Chip({ state, label }: { state: 'done' | 'active' | 'pending' | 'fail' | 'na'; label: string }) {
  const color = state === 'done' ? GREEN : state === 'fail' ? RED : state === 'active' ? NAVY : '#b7c0c6';
  const glyph = state === 'done' ? '✓' : state === 'fail' ? '✕' : '';
  const dim = state === 'pending' || state === 'na';
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: dim ? '#9aa4ab' : INK }}>
      <span style={{ width: 20, height: 20, borderRadius: '50%', background: dim ? 'transparent' : color, border: dim ? `2px solid ${color}` : 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flex: 'none', animation: state === 'active' ? 'sp-pulse 1.1s ease-in-out infinite' : undefined }}>{glyph}</span>
      {label}
    </span>
  );
}

function InfoRow({ label, value, mono, bold }: { label: string; value: string; mono?: boolean; bold?: boolean }) {
  return (
    <tr>
      <td style={{ padding: '11px 0', color: MUTED, width: 190, verticalAlign: 'top', borderBottom: `1px solid ${LINE}` }}>{label}</td>
      <td className={mono ? 'wc-num' : undefined} style={{ padding: '11px 0', color: INK, fontWeight: bold ? 700 : 500, borderBottom: `1px solid ${LINE}` }}>{value}</td>
    </tr>
  );
}
